import { AuthStateModel, LoginAction, LogoutAction } from './auth.state.model';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { AppActions } from '../../app.actions';
import { Editor } from '../../editor/editor.model';
import { environment } from '../../../environments/environment';
import { normalizeInternalRedirect } from './auth.state.model';
import { DocumentationService, DocumentationVersion } from '../services/documentation.service';

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    isAuthenticated: false,
    token: null,
    editor: null
  }
})
@Injectable()
export class AuthState {
  private store = inject(Store);
  private authService = inject(AuthService);
  private router = inject(Router);
  private documentationService = inject(DocumentationService);

  @Selector()
  static getToken(state: AuthStateModel): string | null {
    return state.token;
  }

  @Selector()
  static getIsAuthenticated(state: AuthStateModel): boolean {
    return state.isAuthenticated;
  }

  @Selector()
  static getEditor(state: AuthStateModel): Editor | null {
    return state.editor;
  }

  @Action(AppActions.RestoreAuthState)
  restoreAuthState(ctx: StateContext<AuthStateModel>) {
    const token = localStorage.getItem('access_token');
    const isAuthenticated = token !== null;
    const loadedEditor = JSON.parse(localStorage.getItem('editor') || 'null') as Editor | null;

    ctx.patchState({ isAuthenticated, token, editor: loadedEditor });
  }

  @Action(LoginAction)
  login(ctx: StateContext<AuthStateModel>, action: LoginAction) {
    this.authService.login(action.payload).subscribe({
      next: (dto) => {
        localStorage.setItem('access_token', dto.token);
        localStorage.setItem('editor', JSON.stringify(dto.user));
        localStorage.setItem('isAuthenticated', 'true');

        ctx.patchState({
          isAuthenticated: true,
          token: dto.token,
          editor: dto.user,
        });

        this.store.dispatch(new AppActions.LoadInitialData());

        const targetRedirect = action.redirectTo || localStorage.getItem('redirect_url');
        localStorage.removeItem('redirect_to');
        localStorage.removeItem('redirect_url');

        // V1 must be checked first, as '/dev-area/apidocumentation' is a prefix of '/dev-area/apidocumentation-v1'
        let documentationVersion: DocumentationVersion | null = null;
        if (targetRedirect) {
          if (targetRedirect.includes('/api/v1/documentation') || targetRedirect.includes('/dev-area/apidocumentation-v1')) {
            documentationVersion = 'v1';
          }
          else if (targetRedirect.includes('/api/v2/documentation') || targetRedirect.includes('/dev-area/apidocumentation')) {
            documentationVersion = 'v2';
          }
        }

        if (documentationVersion && dto.user?.role === 'zavAdmin') {
          if (environment.isLocal) {
            window.location.href = `${environment.backendUrl}/api/${documentationVersion}/documentation`;
            return;
          }

          this.documentationService.getDocumentationAuthUrl(documentationVersion).subscribe({
            next: (res) => {
              if (res.url) {
                window.location.href = res.url;
              }
              else {
                this.router.navigateByUrl('/dashboard');
              }
            },
            error: () => {
              this.router.navigateByUrl('/dashboard');
            }
          });
          return;
        }

        const redirectUrl = normalizeInternalRedirect(targetRedirect, '/dashboard');
        this.router.navigateByUrl(redirectUrl);
      },
    });
  }

  @Action(LogoutAction)
  logout(ctx: StateContext<AuthStateModel>) {
    const internalLogout = () => {
      // Save current URL so the user is redirected back after login.
      // Only set if not already saved (auth-sync.service sets it synchronously first).
      if (!this.router.url.includes('/login') && !localStorage.getItem('redirect_url')) {
        localStorage.setItem('redirect_url', this.router.url);
      }
      this.router.navigate(['/login']);

      ctx.setState({
        isAuthenticated: false,
        token: null,
        editor: null,
      });

      localStorage.removeItem('access_token');
      localStorage.removeItem('editor');
      localStorage.removeItem('isAuthenticated');
    };
    this.authService
      .logout()
      .subscribe({
        next: () => {
          if (!environment.isProd) {
            console.log('Server logout successful');
          }
          internalLogout();
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: (err: any) => {
          internalLogout();

          if (!environment.isProd) {
            console.warn('Server logout not successful with error ', err);
          }
        },
      });
  }
}
